import { DollarSign, Plus, Trash2 } from 'lucide-react';
import { getIcon } from '../../../../../../common/src/lib/icon-map';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface Service {
  iconName: string;
  label: string;
  description: string;
  extraPrice?: string | null;
  order: number;
}

interface ServiceMarketingEditorProps {
  services: Service[];
  onChange: (services: Service[]) => void;
}

export function ServiceMarketingEditor({ services, onChange }: ServiceMarketingEditorProps) {
  const addService = () => {
    onChange([
      ...services,
      {
        iconName: 'CheckCircle',
        label: 'Nouveau Service',
        description: 'Description du service',
        order: services.length + 1,
      },
    ]);
  };

  const updateService = (idx: number, patch: Partial<Service>) => {
    const next = [...services];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeService = (idx: number) => {
    onChange(services.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        {services.map((service, idx) => {
          const IconComponent = getIcon(service.iconName);
          const isExtra = !!service.extraPrice;

          return (
            <div
              key={idx}
              className={`group relative flex items-center gap-6 p-6 border-2 rounded-[24px] transition-all bg-white shadow-sm hover:shadow-md ${isExtra ? 'border-amber-100' : 'border-gray-100 hover:border-[#00758d]/20'}`}
            >
              {/* Icon & Status */}
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${isExtra ? 'bg-amber-50 text-amber-600' : 'bg-[#00758d]/5 text-[#00758d]'}`}
              >
                <IconComponent size={28} />
              </div>

              {/* Editing Fields */}
              <div className="flex-1 grid grid-cols-12 gap-6 items-end">
                {/* Title & Description */}
                <div className="col-span-7 space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                        Nom du service
                      </Label>
                    </div>
                    <Input
                      value={service.label}
                      onChange={(e) => updateService(idx, { label: e.target.value })}
                      className="h-10 text-base font-bold text-[#00303c] border-none px-0 focus-visible:ring-0 shadow-none bg-transparent placeholder:opacity-20"
                      placeholder="Ex: WiFi haut débit"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400 opacity-60">
                        Description marketing
                      </Label>
                    </div>
                    <Input
                      value={service.description}
                      onChange={(e) => updateService(idx, { description: e.target.value })}
                      className="h-8 text-sm font-medium text-gray-500 border-none px-0 focus-visible:ring-0 shadow-none bg-transparent placeholder:opacity-20"
                      placeholder="Ex: Connexion fibre optique stable et rapide"
                    />
                  </div>
                </div>

                {/* Price / Option Status */}
                <div className="col-span-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <DollarSign size={10} className="text-gray-400" />
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                      Sur-coût / Statut
                    </Label>
                  </div>
                  <div className="relative">
                    <Input
                      value={service.extraPrice || ''}
                      onChange={(e) => updateService(idx, { extraPrice: e.target.value || null })}
                      placeholder="Inclus dans le prix"
                      className={`h-11 px-4 text-xs font-black rounded-xl border-2 transition-all shadow-none ${isExtra ? 'border-amber-200 bg-amber-50/30 text-amber-700 focus:border-amber-400' : 'border-gray-100 bg-gray-50 text-gray-400 focus:border-[#00758d]/30'}`}
                    />
                    {!isExtra && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded bg-white text-[8px] font-black text-gray-300 uppercase">
                        Gratuit
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delete Button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeService(idx)}
                className="w-10 h-10 rounded-xl text-red-200 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
              >
                <Trash2 size={18} />
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addService}
        className="w-full h-16 border-2 border-dashed border-gray-100 rounded-[24px] text-gray-400 hover:border-[#00758d]/20 hover:text-[#00758d] hover:bg-gray-50 transition-all font-bold uppercase tracking-widest text-[10px]"
      >
        <Plus size={20} className="mr-2" /> Ajouter un service ou un avantage marketing
      </Button>
    </div>
  );
}
